import { apiClient } from './client';
import type {
  EntityAttachment,
  CreateEntityAttachmentInput,
  UpdateEntityAttachmentInput,
  EntityAttachmentUploadUrlResponse,
} from '@peles/shared';

export async function listEntityAttachments(
  entityType: string,
  entityId: string,
): Promise<EntityAttachment[]> {
  const { data } = await apiClient.get<EntityAttachment[]>('/entity-attachments', {
    params: { entityType, entityId },
  });
  return data;
}

/**
 * Batch-fetch attachments for many entities of the same type in one request.
 * Returns a flat list; each attachment carries its own `entityId` for grouping.
 */
export async function listEntityAttachmentsForEntities(
  entityType: string,
  entityIds: string[],
): Promise<EntityAttachment[]> {
  if (entityIds.length === 0) return [];
  const { data } = await apiClient.get<EntityAttachment[]>('/entity-attachments', {
    params: { entityType, entityIds: entityIds.join(',') },
  });
  return data;
}

export async function getAttachmentUploadUrl(
  fileName: string,
  mimeType: string,
): Promise<EntityAttachmentUploadUrlResponse> {
  const { data } = await apiClient.post<EntityAttachmentUploadUrlResponse>(
    '/entity-attachments/upload-url',
    { fileName, mimeType },
  );
  return data;
}

export async function createEntityAttachment(
  input: CreateEntityAttachmentInput,
): Promise<EntityAttachment> {
  const { data } = await apiClient.post<EntityAttachment>('/entity-attachments', input);
  return data;
}

export async function updateEntityAttachment(
  id: string,
  input: UpdateEntityAttachmentInput,
): Promise<EntityAttachment> {
  const { data } = await apiClient.patch<EntityAttachment>(`/entity-attachments/${id}`, input);
  return data;
}

/**
 * Remove an attachment from the record it is shown on. A file filed elsewhere
 * and only linked to this record (a vendor's upload, filed under the event)
 * loses that link and stays where it is filed.
 */
export async function deleteEntityAttachment(
  id: string,
  record?: { entityType: string; entityId: string },
): Promise<void> {
  await apiClient.delete(`/entity-attachments/${id}`, record ? { params: record } : undefined);
}
